import { useMemo, useState } from "react";
import { yerelGunBasi } from "@koclukpanel/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  FiltreKarti,
  Ikon,
  Iskelet,
  MetinAlani,
  Rozet,
  Secim,
  cn,
  Kart,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  planKaldir,
  planKoy,
  ogrenciKaynaklari,
  planTakvimi,
  planToplu,
  type KonuPlani as KonuPlaniSatiri,
  type PlanDurumu,
  type TopluPlanSonucu,
} from "./api";
import { SayfaBasligiEki } from "./Kabuk";

/**
 * KONU BİTİRME TAKVİMİ — F-CUR (§4.7b)
 *
 * ================================================================
 * BU EKRAN 04.09.2026'YA KADAR HİÇ YOKTU
 * ================================================================
 * Modülün dört ucu da sunucuda yazılmıştı (`GET /plan`,
 * `POST /plan/:kayitId`, `POST /plan/toplu`, `DELETE /plan/:kayitId`)
 * ve `api.ts`'te tek satır karşılığı yoktu. Öğrenciye "Türev'i kasım
 * ortasına kadar bitirelim" demenin panelde hiçbir yolu yoktu.
 *
 * ================================================================
 * PLAN, HAFTALIK PROGRAMIN YERİNE GEÇMEZ
 * ================================================================
 * Haftalık program bir haftayı gün gün planlıyor; bu ekran "hangi
 * konu ne zamana kadar" ufkunu tutuyor. Plan haftalık programı
 * UYARMIYOR, yalnızca hazırlarken görünüyor (F-CUR-08).
 *
 * ================================================================
 * PLANSIZ KONU BİR EKSİK DEĞİL — F-CUR-05
 * ================================================================
 * Uç plansız konuları da döndürüyor ve ekran onları kırmızı
 * göstermiyor. Her konunun planı olmak zorunda değil; "planlanmamış"
 * ile "gecikmiş" aynı şey değil.
 */

const DURUM_ADI: Record<PlanDurumu, string> = {
  PLANSIZ: "Planlanmadı",
  PLANLANDI: "Planlandı",
  DEVAM: "Devam ediyor",
  GECIKTI: "Gecikti",
  TAMAMLANDI: "Tamamlandı",
};

const DURUM_TONU: Record<PlanDurumu, "notr" | "bilgi" | "onay" | "uyari" | "hata"> = {
  PLANSIZ: "notr",
  PLANLANDI: "bilgi",
  DEVAM: "bilgi",
  GECIKTI: "hata",
  TAMAMLANDI: "onay",
};

const tarih = (d: string | null): string =>
  d === null ? "—" : new Date(d).toLocaleDateString("tr-TR");

const bugun = (): string => yerelGunBasi().toISOString().slice(0, 10);

export function KonuPlani({
  ogrenciId,
  ogrenciAdi,
  onGeri,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  onGeri?: () => void;
}) {
  const qc = useQueryClient();
  const [ders, setDers] = useState("");
  const [yalnizPlanli, setYalnizPlanli] = useState(false);
  const [acik, setAcik] = useState<string | null>(null);
  const [topluAcik, setTopluAcik] = useState(false);
  // Yıl/ay süzgeci plan BAŞLANGIÇ tarihine bakar; sunucu çağrısı değil, görünüm süzgeci.
  const [yil, setYil] = useState("");
  const [ay, setAyFiltresi] = useState("");

  const liste = useQuery({
    queryKey: ["konu-plani", ogrenciId, ders, yalnizPlanli],
    queryFn: () => planTakvimi(ogrenciId, { ders: ders || undefined, yalnizPlanli }),
    retry: false,
  });

  const yenile = (): void => {
    void qc.invalidateQueries({ queryKey: ["konu-plani", ogrenciId] });
  };

  const dersler = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of liste.data ?? []) m.set(s.dersId, s.ders);
    return [...m].map(([id, ad]) => ({ id, ad })).sort((a, b) => a.ad.localeCompare(b.ad, "tr"));
  }, [liste.data]);

  if (liste.isPending) return <Iskelet satir={6} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Konu planı alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (topluAcik) {
    return (
      <TopluPlan
        ogrenciId={ogrenciId}
        satirlar={liste.data}
        onKapat={() => setTopluAcik(false)}
        onBitti={() => {
          setTopluAcik(false);
          yenile();
        }}
      />
    );
  }

  const gecikmis = liste.data.filter((s) => s.durum === "GECIKTI").length;
  const yillar = [
    ...new Set(
      liste.data.filter((s) => s.baslangic !== null).map((s) => (s.baslangic ?? "").slice(0, 4)),
    ),
  ].sort();
  const tarihSuzgeciVar = yil !== "" || ay !== "";
  const planliSatirlar = liste.data
    .filter((s) => s.baslangic !== null)
    .filter((s) => yil === "" || (s.baslangic ?? "").slice(0, 4) === yil)
    .filter((s) => ay === "" || (s.baslangic ?? "").slice(5, 7) === ay)
    .sort((x, y) => (x.baslangic ?? "").localeCompare(y.baslangic ?? ""));
  // Tarih süzgeci açıkken plansız konuların tarihi yok: "bu ayda" sayılamazlar, gizlenir.
  const planSizSatirlar = tarihSuzgeciVar ? [] : liste.data.filter((s) => s.baslangic === null);
  /** Ay başlığı sırası: plan başlangıç tarihinin yıl-ay anahtarı (`2026-02`). */
  const aylar = [...new Set(planliSatirlar.map((s) => (s.baslangic ?? "").slice(0, 7)))];

  return (
    <div className="flex flex-col gap-6">
      {/*
        UNİKOÇ MÜFREDAT PLANLAMA (sürüm 9a B2; tarama-1 §3.2): başlık bandında açıklama, altında
        süzgeç kartı, altında "Planlar" bölümü: AY BAŞLIKLI gruplar, her plan bir KART ve
        TAMAMLANAN plan YEŞİL. Eski paneldeki "taslak oluştur → toplu kaydet" akışının karşılığı
        burada "Toplu planla" (ön izlemeli) ve satır içi plan formu; veri ve uçlar aynı.
      */}
      <SayfaBasligiEki
        aciklama="Konuları ne zaman bitireceğini planla; tamamlananlar yeşil görünür."
        eylemler={
          <>
            {onGeri && (
              <Buton gorunum="kesikli" onClick={onGeri}>
                ← İş listesi
              </Buton>
            )}
            <Buton onClick={() => setTopluAcik(true)}>Toplu planla</Buton>
          </>
        }
      />

      <FiltreKarti baslik="Öğrenci ve Filtreler" ikon="ogrenci" sutun={3}>
        <div className="flex flex-col gap-1.5">
          <span className="text-metin-koyu text-sm font-semibold">Öğrenci</span>
          <span className="text-metin-koyu min-h-dokunma border-mor-kenar bg-notr-0 flex items-center rounded-[0.75rem] border px-3 text-sm font-semibold">
            {ogrenciAdi}
          </span>
        </div>
        <Secim etiket="Ders" value={ders} onChange={(e) => setDers(e.target.value)}>
          <option value="">Bütün dersler</option>
          {dersler.map((d) => (
            <option key={d.id} value={d.id}>
              {d.ad}
            </option>
          ))}
        </Secim>
        <Secim etiket="Yıl" value={yil} onChange={(e) => setYil(e.target.value)}>
          <option value="">Bütün yıllar</option>
          {yillar.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Secim>
        <Secim etiket="Ay" value={ay} onChange={(e) => setAyFiltresi(e.target.value)}>
          <option value="">Bütün aylar</option>
          {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((m) => (
            <option key={m} value={m}>
              {new Date(2026, Number(m) - 1, 15).toLocaleDateString("tr-TR", { month: "long" })}
            </option>
          ))}
        </Secim>
        <label className="min-h-dokunma text-metin-koyu flex items-center gap-2 self-end text-sm">
          <input
            type="checkbox"
            className="accent-mor-500 size-4"
            checked={yalnizPlanli}
            onChange={(e) => setYalnizPlanli(e.target.checked)}
          />
          Yalnızca planlı konular
        </label>
      </FiltreKarti>

      {/*
        GECİKME SAYISI ÖNCE. Uzman bu ekrana "neyin arkasında kaldık" diye bakıyor; satırların arasında
        aramak zorunda kalmamalı. Sıfırsa hiç yazılmıyor — "0 gecikme" cümlesi bir bilgi değil, gürültü.
      */}
      {gecikmis > 0 && (
        <p className="bg-eksik-zemin text-eksik-metin rounded-[1rem] px-4 py-3 text-sm">
          <strong>{gecikmis}</strong> konu planlanan bitişi geçti.
        </p>
      )}

      {liste.data.length === 0 ? (
        <BosDurum
          baslik={yalnizPlanli ? "Planlı konu yok" : "Konu yok"}
          aciklama={
            yalnizPlanli
              ? "Bu öğrencide henüz planlanmış konu bulunmuyor."
              : "Öğrenciye müfredat atanmadan konu planı kurulamaz."
          }
        />
      ) : (
        <section aria-labelledby="planlar-baslik" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2
              id="planlar-baslik"
              className="text-metin-koyu flex items-center gap-2 text-2xl font-bold"
            >
              <Ikon ad="gorevler" className="size-6" />
              Planlar
            </h2>
            <p className="text-metin-ikincil flex items-center gap-1.5 text-sm">
              <Ikon ad="bilgi" className="size-4" />
              Tamamlananlar yeşil görünür.
            </p>
          </div>

          {aylar.map((ay) => {
            const kayitlar = planliSatirlar.filter((s) => (s.baslangic ?? "").startsWith(ay));
            return (
              <Kart
                gorunum="unk"
                key={ay}
                baslik={ayAdi(ay)}
                ikon="takvim"
                eylem={
                  <span className="bg-mor-50 text-metin-koyu rounded-rozet px-3 py-1 text-sm font-bold">
                    {kayitlar.length} kayıt
                  </span>
                }
              >
                <ul className="flex flex-col gap-3">
                  {kayitlar.map((s) => (
                    <PlanKarti
                      key={s.id}
                      satir={s}
                      acik={acik === s.id}
                      onAc={() => setAcik(acik === s.id ? null : s.id)}
                      ogrenciId={ogrenciId}
                      onBitti={yenile}
                    />
                  ))}
                </ul>
              </Kart>
            );
          })}

          {planSizSatirlar.length > 0 && (
            <Kart
              gorunum="unk"
              baslik="Planlanmamış konular"
              ikon="mufredat"
              eylem={
                <span className="bg-mor-50 text-metin-koyu rounded-rozet px-3 py-1 text-sm font-bold">
                  {planSizSatirlar.length} kayıt
                </span>
              }
            >
              <ul className="flex flex-col gap-3">
                {planSizSatirlar.map((s) => (
                  <PlanKarti
                    key={s.id}
                    satir={s}
                    acik={acik === s.id}
                    onAc={() => setAcik(acik === s.id ? null : s.id)}
                    ogrenciId={ogrenciId}
                    onBitti={yenile}
                  />
                ))}
              </ul>
            </Kart>
          )}

          <p className="text-metin-ikincil text-right text-sm">
            {liste.data.length} kayıt gösteriliyor
          </p>
        </section>
      )}
    </div>
  );
}

/** "2026-02" → "Şubat 2026" (tr-TR, yerel ay başı; saat dilimi kayması yok: gün 15). */
function ayAdi(anahtar: string): string {
  const [y, m] = anahtar.split("-").map(Number);
  if (!y || !m) return anahtar;
  return new Date(y, m - 1, 15).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
}

/**
 * PLAN KARTI — UNİKOÇ "plan satırı kartı" (tarama-1 §3.2): ders (kalın büyük harf) / konu,
 * saat ikonlu tarih · gün, kitap ikonlu kaynak listesi (BÜYÜK HARF), altta kesikli düğmeler.
 * TAMAMLANAN plan açık yeşil zeminli; geciken kırmızı ton (kenar + durum metni, renk tek başına
 * değil). Satır içi plan formu kartın altında açılıyor.
 */
function PlanKarti({
  satir: s,
  acik,
  onAc,
  ogrenciId,
  onBitti,
}: {
  satir: KonuPlaniSatiri;
  acik: boolean;
  onAc: () => void;
  ogrenciId: string;
  onBitti: () => void;
}) {
  const tamam = s.durum === "TAMAMLANDI";
  const gecikti = s.durum === "GECIKTI";
  return (
    <li
      className={cn(
        "flex flex-col gap-2 rounded-[0.875rem] border p-4",
        tamam
          ? "border-tamam-nokta/30 bg-tamam-zemin"
          : gecikti
            ? "border-eksik-nokta/30 bg-eksik-zemin"
            : "border-mor-kenar bg-notr-0",
      )}
    >
      <p className="text-metin-koyu flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.9375rem]">
        <Ikon ad="mufredat" className="size-4 shrink-0" />
        <strong className="uppercase">{s.ders}</strong>
        <span aria-hidden="true">/</span>
        <span>{s.konu}</span>
      </p>
      {s.baslangic !== null && (
        <p className="text-metin-ikincil flex flex-wrap items-center gap-x-2 text-sm">
          <Ikon ad="bekliyor" className="size-4 shrink-0" />
          {tarih(s.baslangic)}
          {s.sureGun !== null && <> • {s.sureGun} gün</>}
          {s.bitis !== null && <> · bitiş {tarih(s.bitis)}</>}
          {/* `null` = plan YOK ya da konu BİTTİ; "0 gün" "bugün bitiyor" demek olurdu. */}
          {s.kalanGun !== null && <> · kalan {s.kalanGun} gün</>}
        </p>
      )}
      {s.kaynaklar.length > 0 && (
        <p className="text-metin-koyu flex items-start gap-2 text-sm uppercase">
          <Ikon ad="defter" className="mt-0.5 size-4 shrink-0" />
          {s.kaynaklar.map((k) => k.ad).join(", ")}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Rozet ton={DURUM_TONU[s.durum]}>
          {DURUM_ADI[s.durum]}
          {s.gecKapandi ? " · geç kapandı" : ""}
        </Rozet>
        <Buton gorunum="kesikli" onClick={onAc}>
          {acik ? "Kapat" : s.durum === "PLANSIZ" ? "Plan koy" : "Düzenle"}
        </Buton>
      </div>
      {acik && <PlanFormu ogrenciId={ogrenciId} satir={s} onBitti={onBitti} />}
    </li>
  );
}

// ---------------------------------------------------------------
// TEK KONU — PLAN KOY / REVİZE ET / KALDIR
// ---------------------------------------------------------------

function PlanFormu({
  ogrenciId,
  satir,
  onBitti,
}: {
  ogrenciId: string;
  satir: KonuPlaniSatiri;
  onBitti: () => void;
}) {
  const [baslangic, setBaslangic] = useState(satir.baslangic?.slice(0, 10) ?? bugun());
  const [sure, setSure] = useState(String(satir.sureGun ?? 7));
  const [kanal, setKanal] = useState(satir.videoKanali ?? "");
  const [not, setNot] = useState(satir.not ?? "");
  const [kaldirAcik, setKaldirAcik] = useState(false);
  const [gerekce, setGerekce] = useState("");
  const [seciliKaynaklar, setSeciliKaynaklar] = useState<string[]>(
    satir.kaynaklar.map((k) => k.id),
  );

  // Kaynak listesi ayrı izne bağlı; okunamazsa `kaynakIdler` HİÇ gönderilmez
  // (`undefined` = dokunma) — yoksa boş liste mevcut bağları silerdi.
  const kaynaklar = useQuery({
    queryKey: ["ogrenci-kaynak", ogrenciId],
    queryFn: () => ogrenciKaynaklari(ogrenciId),
    retry: false,
  });
  const kaynakSecenekleri = kaynaklar.data ?? [];

  const koy = useMutation({
    mutationFn: () =>
      planKoy(ogrenciId, satir.id, {
        baslangic,
        sureGun: Number(sure),
        videoKanali: kanal.trim() || null,
        not: not.trim() || null,
        // Arşive giden kaynak listede görünmez; gönderilseydi sunucu GECERSIZ_KAYNAK derdi.
        ...(kaynaklar.data
          ? {
              kaynakIdler: seciliKaynaklar.filter((id) =>
                kaynakSecenekleri.some((k) => k.id === id),
              ),
            }
          : {}),
      }),
    onSuccess: onBitti,
  });

  const kaldir = useMutation({
    mutationFn: () => planKaldir(ogrenciId, satir.id, gerekce.trim()),
    onSuccess: () => {
      setKaldirAcik(false);
      setGerekce("");
      onBitti();
    },
  });

  const sureSayi = Number(sure);
  const sureGecerli = Number.isInteger(sureSayi) && sureSayi >= 1 && sureSayi <= 365;

  return (
    <div className="rounded-kart border-notr-200 shadow-kart flex flex-col gap-4 border p-4">
      {satir.planlayan !== null && (
        <p className="text-notr-600 text-xs">
          {satir.planlayan} planladı · {tarih(satir.planlanmaZamani)}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Alan
          etiket="Başlangıç"
          type="date"
          value={baslangic}
          onChange={(e) => setBaslangic(e.target.value)}
        />
        <Alan
          etiket="Süre (gün)"
          type="number"
          min={1}
          max={365}
          value={sure}
          onChange={(e) => setSure(e.target.value)}
          hata={sure !== "" && !sureGecerli ? "1 ile 365 arasında olmalı." : undefined}
          yardim="Bitiş tarihini sunucu hesaplıyor."
        />
        <Alan
          etiket="Video kanalı (isteğe bağlı)"
          value={kanal}
          onChange={(e) => setKanal(e.target.value)}
          yardim="Öğrencinin bu konuyu hangi kanaldan izleyeceği."
        />
      </div>

      <MetinAlani
        etiket="Not (isteğe bağlı)"
        rows={2}
        value={not}
        onChange={(e) => setNot(e.target.value)}
      />

      {kaynakSecenekleri.length > 0 ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-metin-koyu text-sm font-semibold">Kaynak kitaplar</legend>
          <ul className="flex flex-col gap-1">
            {kaynakSecenekleri.map((k) => (
              <li key={k.id}>
                <label className="min-h-dokunma text-metin-koyu flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="accent-mor-500 size-4"
                    checked={seciliKaynaklar.includes(k.id)}
                    onChange={(e) =>
                      setSeciliKaynaklar((onceki) =>
                        e.target.checked ? [...onceki, k.id] : onceki.filter((x) => x !== k.id),
                      )
                    }
                  />
                  {k.ad} <span className="text-notr-600">({k.ders})</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      ) : (
        satir.kaynaklar.length > 0 && (
          <p className="text-notr-600 text-xs">
            Bağlı kaynaklar: {satir.kaynaklar.map((k) => k.ad).join(", ")}
          </p>
        )
      )}

      {koy.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {koy.error instanceof ApiHatasi ? koy.error.message : "Plan kaydedilemedi."}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton yukleniyor={koy.isPending} disabled={!sureGecerli} onClick={() => koy.mutate()}>
          {satir.durum === "PLANSIZ" ? "Planı kaydet" : "Planı güncelle"}
        </Buton>

        {satir.durum !== "PLANSIZ" && !kaldirAcik && (
          <Buton gorunum="sessiz" onClick={() => setKaldirAcik(true)}>
            Planı kaldır
          </Buton>
        )}
      </div>

      {kaldirAcik && (
        <div className="rounded-alan border-notr-200 flex flex-col gap-2 border p-3">
          {/*
            PLANI kaldırır, KONUYU kaldırmaz. Bunu yazmak gerekiyor:
            "kaldır" düğmesine basan uzman konunun öğrenciden
            silineceğini düşünebilir.
          */}
          <p className="text-notr-700 text-sm">
            Yalnızca <strong>plan</strong> kaldırılır; konu öğrencinin müfredatında kalır.
          </p>
          <Alan
            etiket="Kaldırma gerekçesi"
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
            yardim="Denetim kaydına yazılır. En az 3 karakter."
          />
          {kaldir.isError && (
            <p role="alert" className="text-hata-700 text-sm font-medium">
              {kaldir.error instanceof ApiHatasi ? kaldir.error.message : "Plan kaldırılamadı."}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Buton
              gorunum="tehlike"
              yukleniyor={kaldir.isPending}
              disabled={gerekce.trim().length < 3}
              onClick={() => kaldir.mutate()}
            >
              Planı kaldır
            </Buton>
            <Buton gorunum="ikincil" onClick={() => setKaldirAcik(false)}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// TOPLU PLANLAMA — F-CUR-07
// ---------------------------------------------------------------

/**
 * ================================================================
 * ÖNCE ÖN İZLEME, SONRA YAZMA
 * ================================================================
 * Uç iki kez çağrılıyor: `onIzleme: true` hiçbir şey yazmıyor,
 * yalnızca hangi konunun planlanacağını ve hangisinin neden
 * atlanacağını söylüyor. İki yüz konuyu görmeden yazmak, geri
 * alınması zor bir işlem — ve atlanma sebebi ancak sunucu bilir.
 */
function TopluPlan({
  ogrenciId,
  satirlar,
  onKapat,
  onBitti,
}: {
  ogrenciId: string;
  satirlar: readonly KonuPlaniSatiri[];
  onKapat: () => void;
  onBitti: () => void;
}) {
  const [secili, setSecili] = useState<Set<string>>(new Set());
  const [baslangic, setBaslangic] = useState(bugun());
  const [sure, setSure] = useState("7");
  const [onizleme, setOnizleme] = useState<TopluPlanSonucu | null>(null);

  const kalemler = (): Array<{ kayitId: string; baslangic: string; sureGun: number }> =>
    [...secili].map((kayitId) => ({ kayitId, baslangic, sureGun: Number(sure) }));

  const bak = useMutation({
    mutationFn: () => planToplu(ogrenciId, kalemler(), true),
    onSuccess: (c) => setOnizleme(c),
  });

  const uygula = useMutation({
    mutationFn: () => planToplu(ogrenciId, kalemler(), false),
    onSuccess: onBitti,
  });

  const cevir = (id: string): void => {
    const yeni = new Set(secili);
    if (yeni.has(id)) yeni.delete(id);
    else yeni.add(id);
    setSecili(yeni);
    // Seçim değişince ön izleme ESKİDİ. Silmezsek kullanıcı eski
    // listeye bakıp yeni seçimi uygular ve ne yazdığını bilmez.
    setOnizleme(null);
  };

  const sureSayi = Number(sure);
  const sureGecerli = Number.isInteger(sureSayi) && sureSayi >= 1 && sureSayi <= 365;
  const fazla = secili.size > 200;

  return (
    <Kart
      gorunum="unk"
      baslik="Toplu planlama"
      ikon="plan"
      aciklama="Seçilen konulara aynı başlangıç ve süre uygulanır. Yazmadan önce ön izleme gösterilir."
    >
      <SayfaBasligiEki aciklama="Önce konuları seç; taslağı önizle ve toplu kaydet." />
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Başlangıç"
            type="date"
            value={baslangic}
            onChange={(e) => {
              setBaslangic(e.target.value);
              setOnizleme(null);
            }}
          />
          <Alan
            etiket="Süre (gün)"
            type="number"
            min={1}
            max={365}
            value={sure}
            onChange={(e) => {
              setSure(e.target.value);
              setOnizleme(null);
            }}
            hata={sure !== "" && !sureGecerli ? "1 ile 365 arasında olmalı." : undefined}
          />
        </div>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-notr-800 text-sm font-medium">
            Konular ({secili.size} seçili)
          </legend>
          <div className="rounded-alan border-notr-200 max-h-80 overflow-y-auto border p-2">
            {satirlar.map((s) => (
              <label key={s.id} className="text-notr-700 flex items-start gap-2 py-1 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={secili.has(s.id)}
                  onChange={() => cevir(s.id)}
                />
                <span>
                  {s.konu}
                  <span className="text-notr-600 block text-xs">
                    {s.ders} · {DURUM_ADI[s.durum]}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {fazla && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            Tek seferde en fazla 200 konu planlanabilir; {secili.size} seçtiniz.
          </p>
        )}

        {onizleme !== null && onizleme.onIzleme && (
          <div className="rounded-alan bg-notr-50 flex flex-col gap-2 p-3 text-sm">
            <p className="text-notr-900 font-medium">
              {onizleme.uygulanacak.length} konu planlanacak
              {onizleme.uygulanacak.filter((u) => u.revizeMi).length > 0 &&
                ` (${onizleme.uygulanacak.filter((u) => u.revizeMi).length} tanesi REVİZE)`}
            </p>
            {onizleme.atlanan.length > 0 && (
              <div>
                <p className="text-uyari-600 font-medium">
                  {onizleme.atlanan.length} konu atlanacak:
                </p>
                <ul className="text-notr-700 mt-1 flex flex-col gap-0.5">
                  {onizleme.atlanan.map((a) => (
                    <li key={a.kayitId}>· {a.neden}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {(bak.isError || uygula.isError) && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {(bak.error ?? uygula.error) instanceof ApiHatasi
              ? ((bak.error ?? uygula.error) as ApiHatasi).message
              : "İşlem tamamlanamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            gorunum="ikincil"
            yukleniyor={bak.isPending}
            disabled={secili.size === 0 || !sureGecerli || fazla}
            onClick={() => bak.mutate()}
          >
            Ön izleme
          </Buton>

          {/*
            UYGULAMA DÜĞMESİ ÖN İZLEMEDEN SONRA açılıyor. Doğrudan
            uygulanabilseydi ön izleme atlanabilir bir adım olurdu ve
            atlanan konuların sebebi hiç görülmezdi.
          */}
          <Buton
            yukleniyor={uygula.isPending}
            disabled={onizleme === null || secili.size === 0 || !sureGecerli || fazla}
            onClick={() => uygula.mutate()}
          >
            Planları uygula
          </Buton>

          <Buton gorunum="sessiz" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}
